/**
 * Route copy for `src/routes/process` (keys: `routes.process.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  decisionModelEditor: {
    operator: 'Operator',
    always: '— (always)',
    value: 'Value',
    min: 'Min',
    min2: 'min',
    max: 'Max',
    max2: 'max',
    evaluationFailedErrorStatus: 'Evaluation failed (error {{status}}).',
    evaluationFailedTryAgain: 'Evaluation failed. Try again.',
    deterministic: 'Deterministic',
    ai: 'AI',
    default: 'Default',
    noOutput: 'No output',
    testTheDecision: 'Test the decision',
    addAtLeastOneInputTo: 'Add at least one input to test.',
    true: 'true',
    false: 'false',
    evaluating: 'Evaluating…',
    test: 'Test',
    rule: 'rule',
    aiV: 'AI: {{v}}',
    aiUnavailableFallingBackToThe: 'AI unavailable — falling back to the default (no made-up verdict).',
    tableName: 'Table name',
    eGPriorityLevel: 'e.g. Priority level',
    hitPolicy: 'Hit policy',
    howToChooseAmongTheMatching: 'How to choose among the matching rules.',
    mode: 'Mode',
    aiConsultedOnlyOnTheResidual: 'AI consulted only on the residual/ambiguous case.',
    inputs: 'Inputs',
    input: '+ Input',
    inputName: 'Input name',
    type: 'Type',
    deleteTheInputName: 'Delete the input {{name}}',
    noInputs: 'No inputs.',
    rules: 'Rules',
    output: '+ Output',
    rule2: '+ Rule',
    ifName: 'If · {{name}}',
    then: 'Then',
    outputNameKey: 'Output name {{key}}',
    deleteTheOutputKey: 'Delete the output {{key}}',
    outputKeyOfRuleV: 'Output {{key}} of rule {{v}}',
    modeOfRuleV: 'Mode of rule {{v}}',
    inherited: 'inherited',
    deleteRuleV: 'Delete rule {{v}}',
    anEmptyIfCellNoCondition: 'An empty “If” cell = no condition on that input. A rule with no condition at all is a catch-all.',
    defaultOutputIfNoRuleMatches: 'Default output (if no rule matches)',
    value2: 'value',
  },
  nodesDecisionNode: {
    rule: 'rule',
    refV: 'ref. {{v}}',
    noDecisionTable: 'No decision table',
  },
  nodesShared: {
    requiresHumanApproval: 'Requires human approval',
  },
  nodesTaskNode: {
    noPersona: 'No persona',
  },
  processTypes: {
    exclusiveXor: 'Exclusive (XOR)',
    parallelAnd: 'Parallel (AND)',
    inclusiveOr: 'Inclusive (OR)',
    eventBased: 'Event-based',
    complex: 'Complex',
    equals: '= equals',
    notEqual: '≠ not equal',
    lessThan: '< less than',
    lessOrEqual: '≤ less or equal',
    greaterThan: '> greater than',
    greaterOrEqual: '≥ greater or equal',
    inTheList: '∈ in the list',
    text: 'Text',
    count: 'Count',
    boolean: 'Boolean',
    firstRuleOrderMatters: 'First rule (order matters)',
    uniqueOnlyOneMustMatch: 'Unique (only one must match)',
    collectAllMatches: 'Collect (all matches)',
    deterministic: 'Deterministic',
    hybridAiOnTheResidualCase: 'Hybrid (AI on the residual case)',
    ai: 'AI',
    high: 'high',
    start: 'Start',
    processStartingPoint: 'Process starting point',
    task: 'Task',
    aTaskRunByAnAi: 'A task run by an AI persona',
    decision: 'Decision',
    dmnDecisionTableDeterministicAi: 'DMN decision table (deterministic/AI)',
    gateway: 'Gateway',
    branchingXorAndOr: 'Branching (XOR / AND / OR)',
    end: 'End',
    endOfTheProcess: 'End of the process',
    theCanvasIsEmptyDropNodes: 'The canvas is empty — drop nodes from the palette.',
    noStartNode: 'No “Start” node.',
    lengthStartNodesOnlyOneIs: '{{length}} “Start” nodes — only one is recommended.',
    noEndNode: 'No “End” node.',
    noTaskDecisionNothingToCompile: 'No task/decision — nothing to compile into agent work.',
    labelIsNotConnectedToAnything: '“{{label}}” is not connected to anything.',
    labelNoPersonaRoleAssigned: '“{{label}}”: no persona/role assigned.',
    labelNoDecisionTable: '“{{label}}”: no decision table.',
    theGraphContainsACycleA: 'The graph contains a cycle — a process must be acyclic.',
  },
  runVisualizer: {
    couldNotRetrieveThisRunS: 'Could not retrieve this run\'s progress.',
    runStarted: 'Run started',
    compiledNotLaunched: 'Compiled (not launched)',
    run: '· run',
    upd: '· upd.',
    refresh: 'Refresh',
    backToTheEditor: 'Back to the editor',
    processProgress: 'Process progress',
    doneTotalTasks: '{{done}}/{{total}} tasks',
    ready: 'ready',
    inProgress: 'in progress',
    blocked: 'blocked',
    awaitingAppr: 'awaiting appr.',
    completed: 'completed',
    failures: 'failures',
    cancelled: 'cancelled',
    waitingApprovalTaskSAwaitingA: '{{waiting_approval}} task(s) awaiting a human decision.',
    openApprovals: 'Open approvals →',
  },
  workflowStudioPage: {
    filter: 'Filter…',
    removeV: 'Remove {{v}}',
    palette: 'Palette',
    descriptionDragOntoTheCanvasOr: '{{description}} — drag onto the canvas or click to add',
    dragANodeOntoTheCanvas: 'Drag a node onto the canvas (or click to add it), then connect the nodes: a link A→B means “B depends on A”.',
    newProcess: 'New process',
    couldNotLoadThisProcess: 'Could not load this process.',
    start: 'Start',
    end: 'End',
    theProcessNameIsRequired: 'The process name is required.',
    processSaved: 'Process saved.',
    insufficientRightsToSaveThisProcess: 'Insufficient rights to save this process.',
    savingFailedErrorStatus: 'Saving failed (error {{status}}).',
    savingFailed: 'Saving failed.',
    bpmnImportedLengthNodeS: 'BPMN imported — {{length}} node(s).',
    insufficientRightsToImportThisProcess: 'Insufficient rights to import this process.',
    invalidBpmnV: 'Invalid BPMN: {{v}}',
    bpmnImportFailedErrorStatus: 'BPMN import failed (error {{status}}).',
    bpmnImportFailed: 'BPMN import failed.',
    fixTheValidationErrorsBeforeCompiling: 'Fix the validation errors before compiling.',
    processCompiledLengthTaskSV: 'Process compiled — {{length}} task(s){{v}}.',
    insufficientRightsToCompileLaunchThis: 'Insufficient rights to compile/launch this process.',
    compilationNotPossibleV: 'Compilation not possible: {{v}}',
    compilationFailedErrorStatus: 'Compilation failed (error {{status}}).',
    compilationFailed: 'Compilation failed.',
    processDeleted: 'Process deleted.',
    couldNotDelete: 'Could not delete.',
    runName: 'Run — {{name}}',
    liveTrackingEachNodeIsColored: 'Live tracking: each node is colored according to its task\'s state (ready, in progress, completed, awaiting approval, failed).',
    processStudio: 'Process studio',
    composeAnAgenticProcessByDragging: 'Compose an agentic process by dragging and dropping nodes, connect them, then compile it so your AI agents run it.',
    loadAProcess: 'Load a process',
    loading: 'Loading…',
    loadAProcess2: '— Load a process —',
    new: 'New',
    processName: 'Process name',
    description: 'Description',
    whatIsThisProcessFor: 'What is this process for?',
    saving: 'Saving…',
    save: 'Save',
    importing: 'Importing…',
    importBpmn: 'Import BPMN',
    compiling: 'Compiling…',
    compileLaunch: 'Compile & launch',
    delete: 'Delete',
    platformProcessSharedWithEveryTenant: 'Platform process (shared with every tenant — reserved for administrators).',
    platform: 'Platform',
    tenant: 'Tenant',
    backspace: 'Backspace',
    delete2: 'Delete',
    inspector: 'Inspector',
    selectANodeOrALink: 'Select a node or a link to edit its properties.',
    decisionTableLabel: 'Decision table — {{label}}',
    done: 'Done',
    label: 'Label',
    aiPersona: 'AI persona',
    theAgentThatRunsTheTask: 'The agent that runs the task.',
    noneFreeRole: '— None / free role —',
    roleFreeText: 'Role (free text)',
    personaNameResolvedOnTheServer: 'Persona name resolved on the server.',
    eGAiContentManager: 'e.g. AI Content Manager',
    autonomyLevelL0L5: 'Autonomy level (L0-L5)',
    personaSPolicy: '— Persona\'s policy —',
    skillsUnionOnThePersona: 'Skills (union on the persona)',
    noSkillInTheRegistry: 'No skill in the registry.',
    tools: 'Tools',
    noToolInTheRegistry: 'No tool in the registry.',
    masCapabilityOptional: 'MAS capability (optional)',
    eGExecuteAnalyseGenerate: 'e.g. execute, analyse, generate.',
    execute: 'execute',
    instructionsContextOptional: 'Instructions / context (optional)',
    detailWhatTheAgentMustDo: 'Detail what the agent must do at this step…',
    lengthRuleSHitPolicyMode: '{{length}} rule(s) · {{hit_policy}} · {{mode}}',
    noDecisionTableDefined: 'No decision table defined.',
    editTheDecisionTable: 'Edit the decision table',
    createTheDecisionTable: 'Create the decision table',
    modelReferenceOptional: 'Model reference (optional)',
    aNamedDmnModelInsteadOf: 'A named DMN model, instead of an inline table.',
    decTier: 'dec-tier',
    gatewayType: 'Gateway type',
    structuralNodeItRunsNoAgent: 'Structural node — it runs no agent work; it routes the flow.',
    linkDependency: 'Link (dependency)',
    aBTheTargetDependsOn: 'A→B: the target depends on the source.',
    labelOptional: 'Label (optional)',
    eGIfApproved: 'e.g. if approved',
    conditionOptional: 'Condition (optional)',
    branchGuardExpression: 'Branch guard expression.',
    score80: 'score >= 80',
    launched: ' launched',
  },
};

export default en;
